import { LoadingOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Modal, Select, Spin, Divider, Tag } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";
import EditTaskApi from "../../../api/tasks/EditTaskApi";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import { 
  getClickUpStatus, 
  getClickUpTeams, 
  getClickUpSpaces, 
  getClickUpLists, 
  getClickUpTeamMembers 
} from "../../../api/clickup/ClickUpApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";

const { Option } = Select;

function EditTaskModal(props) {
  const [form] = Form.useForm();
  const [submit, setSubmit] = useState(false);
  const [unitPositions, setUnitPositions] = useState([]);
  
  // ClickUp Integration State
  const [clickUpEnabled, setClickUpEnabled] = useState(false);
  const [clickUpTeams, setClickUpTeams] = useState([]);
  const [clickUpSpaces, setClickUpSpaces] = useState([]);
  const [clickUpLists, setClickUpLists] = useState([]);
  const [clickUpMembers, setClickUpMembers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [loadingClickUp, setLoadingClickUp] = useState(false);

  useEffect(() => {
    if (props.visible) {
      // Set basic task fields
      form.setFields([
        {
          name: "name",
          value: props.task?.name,
        },
        { name: "targetDate", value: moment(props.task?.taskEndDate) },
        { name: "startDate", value: props.task?.taskStartDate ? moment(props.task?.taskStartDate) : null },
        { name: "assignTo", value: props.task?.assignedTo || null },
      ]);

      // Set ClickUp fields if they exist
      if (props.task?.clickupSpaceId) {
        form.setFields([
          { name: "clickupSpace", value: props.task.clickupSpaceId },
          { name: "clickupList", value: props.task.clickupListId },
          { name: "clickupAssignee", value: props.task.clickupAssigneeId },
          { name: "clickupPriority", value: props.task.clickupPriority },
          { name: "clickupTags", value: props.task.clickupTags ? JSON.parse(props.task.clickupTags) : [] },
        ]);
      }

      if (props.unitId) {
        loadUnitPositions(props.unitId);
      }
      
      checkClickUpStatus();
    }
  }, [props.visible, props.task]);

  async function checkClickUpStatus() {
    try {
      const statusResponse = await getClickUpStatus();
      if (statusResponse.success && statusResponse.data.enabled) {
        setClickUpEnabled(true);
        await loadClickUpTeams();
      }
    } catch (error) {
      console.error("Failed to check ClickUp status:", error);
    }
  }

  async function loadClickUpTeams() {
    try {
      setLoadingClickUp(true);
      const teamsResponse = await getClickUpTeams();
      if (teamsResponse.success) {
        setClickUpTeams(teamsResponse.data);
        // Auto-select first team if only one exists
        if (teamsResponse.data.length === 1) {
          const teamId = teamsResponse.data[0].id;
          setSelectedTeam(teamId);
          form.setFields([{ name: "clickupTeam", value: teamId }]);
          await loadClickUpSpaces(teamId);
          await loadClickUpMembers(teamId);
        }
      }
    } catch (error) {
      console.error("Failed to load ClickUp teams:", error);
    } finally {
      setLoadingClickUp(false);
    }
  }

  async function loadClickUpSpaces(teamId) {
    try {
      const spacesResponse = await getClickUpSpaces(teamId);
      if (spacesResponse.success) {
        setClickUpSpaces(spacesResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp spaces:", error);
    }
  }

  async function loadClickUpLists(spaceId) {
    try {
      const listsResponse = await getClickUpLists(spaceId);
      if (listsResponse.success) {
        setClickUpLists(listsResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp lists:", error);
    }
  }

  async function loadClickUpMembers(teamId) {
    try {
      const membersResponse = await getClickUpTeamMembers(teamId);
      if (membersResponse.success) {
        setClickUpMembers(membersResponse.data);
      }
    } catch (error) {
      console.error("Failed to load ClickUp members:", error);
    }
  }

  async function handleClickUpTeamChange(teamId) {
    setSelectedTeam(teamId);
    setClickUpSpaces([]);
    setClickUpLists([]);
    setClickUpMembers([]);
    form.setFields([
      { name: "clickupSpace", value: null },
      { name: "clickupList", value: null },
      { name: "clickupAssignee", value: null },
    ]);
    
    if (teamId) {
      await loadClickUpSpaces(teamId);
      await loadClickUpMembers(teamId);
    }
  }

  async function handleClickUpSpaceChange(spaceId) {
    setSelectedSpace(spaceId);
    setClickUpLists([]);
    form.setFields([
      { name: "clickupList", value: null },
    ]);
    
    if (spaceId) {
      await loadClickUpLists(spaceId);
    }
  }

  async function loadUnitPositions(unitId) {
    const apiResponse = await GetUnitPositionsApi2(unitId);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data || []);
    }
  }

  function handleCancel() {
    form.resetFields();
    // Reset ClickUp state
    setClickUpSpaces([]);
    setClickUpLists([]);
    setClickUpMembers([]);
    setSelectedTeam(null);
    setSelectedSpace(null);
    props.handleCancel();
  }
  async function onFinish(values) {
    setSubmit(true);
    
    // Prepare ClickUp data if fields are filled
    let clickUpData = null;
    if (clickUpEnabled && values.clickupList && values.clickupSpace) {
      clickUpData = {
        spaceId: values.clickupSpace,
        listId: values.clickupList,
        assigneeId: values.clickupAssignee || null,
        priority: values.clickupPriority || 3,
        tags: values.clickupTags || [],
      };
    }

    const apiResponse = await EditTaskApi(
      props.task?.id,
      values.name,
      values.targetDate,
      values.startDate,
      values.assignTo,
      clickUpData
    );
    
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      const message = clickUpData 
        ? "Task updated successfully and synced to ClickUp" 
        : "Task updated successfully";
      openNotificationSuccess("bottomRight", message);
      props.handleOk();
    } else {
      openNotificationError("bottomRight", `Couldn't update task. Try again later.`);
    }
    handleCancel();
    setSubmit(false);
  }
  return (
    <Modal
      title="Edit Task"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        name="editTask"
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        requiredMark={false}
        form={form}
      >
        <Form.Item
          label="Task*"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter a Task!",
            },
          ]}
        >
          <Input placeholder="Task name" />
        </Form.Item>
        <Form.Item label="Start date" name="startDate">
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item
          label="Target date*"
          name="targetDate"
          rules={[
            {
              required: true,
              message: "Please enter target date!",
            },
          ]}
        >
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item label="Assign to" name="assignTo">
          <Select
            showSearch
            placeholder="Select position"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
          >
            {unitPositions.map((position) => {
              return (
                <Option key={position.id} value={position.id}>
                  {(position.user?.firstName
                    ? position.user?.firstName + ", "
                    : "") + position.designation?.name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* ClickUp Integration Fields */}
        {clickUpEnabled && (
          <>
            <Divider>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                ClickUp Integration <Tag color="blue">Optional</Tag>
              </span>
            </Divider>
            
            <Form.Item label="ClickUp Team" name="clickupTeam">
              <Select
                placeholder="Select ClickUp team"
                loading={loadingClickUp}
                onChange={handleClickUpTeamChange}
                allowClear
              >
                {clickUpTeams.map((team) => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Space" name="clickupSpace">
              <Select
                placeholder="Select ClickUp space"
                onChange={handleClickUpSpaceChange}
                disabled={!selectedTeam}
                allowClear
              >
                {clickUpSpaces.map((space) => (
                  <Option key={space.id} value={space.id}>
                    {space.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp List" name="clickupList">
              <Select
                placeholder="Select ClickUp list"
                disabled={!selectedSpace}
                allowClear
              >
                {clickUpLists.map((list) => (
                  <Option key={list.id} value={list.id}>
                    {list.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Assignee" name="clickupAssignee">
              <Select
                placeholder="Select ClickUp assignee"
                disabled={!selectedTeam}
                allowClear
              >
                {clickUpMembers.map((member) => (
                  <Option key={member.id} value={member.id}>
                    {member.username} ({member.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Priority" name="clickupPriority">
              <Select placeholder="Select priority" allowClear>
                <Option value={1}>🔴 Urgent</Option>
                <Option value={2}>🟡 High</Option>
                <Option value={3}>🟢 Normal</Option>
                <Option value={4}>⚪ Low</Option>
              </Select>
            </Form.Item>

            <Form.Item label="ClickUp Tags" name="clickupTags">
              <Select
                mode="tags"
                placeholder="Add tags (press Enter to add)"
                allowClear
              />
            </Form.Item>
          </>
        )}
        <Form.Item style={{ marginBottom: 0 }}>
          <div className=" flex justify-end">
            <Button className="mr-3 w-28" onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              className="btn w-28"
              type="primary"
              htmlType={submit && "submit"}
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                `Save`
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default EditTaskModal;
